// PlumbingDiagnose — AI Leak Diagnose with drag-and-drop photo upload
// and optional "attach to job" so the verdict lands right on the job
// record (description + estimate + status:diagnosed).
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  Drop, Sparkle, Warning, CheckCircle, Package, PaperPlaneTilt,
  ShieldWarning, Trophy, ArrowRight, X, Camera, UploadSimple, Image as ImageIcon,
  Plus, Link as LinkIcon,
} from "@phosphor-icons/react";

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const MAX_MB = 8;

const CONFIDENCE = {
  low:    { cls: "bg-amber-100 text-amber-800", label: "LOW CONFIDENCE" },
  medium: { cls: "bg-slate-100 text-slate-700", label: "MEDIUM CONFIDENCE" },
  high:   { cls: "bg-emerald-100 text-emerald-800", label: "HIGH CONFIDENCE" },
};

export default function PlumbingDiagnose() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ description: "", address: "", job_id: sp.get("job") || "" });
  const [photo, setPhoto] = useState(null);       // File
  const [photoPreview, setPhotoPreview] = useState(null); // data URL
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [actionOpen, setActionOpen] = useState(null); // "attach" | "create" | null
  const [attachJobId, setAttachJobId] = useState("");
  const [newJob, setNewJob] = useState({ property_address: "", unit: "", customer_name: "", customer_phone: "" });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/plumbing/plumbing-jobs"); setJobs(r.data.jobs || []); }
      catch { /* ignore */ }
    })();
    // Pick up a photo stashed by the dashboard hero drop-target
    try {
      const raw = sessionStorage.getItem("pending_diagnose_photo");
      if (raw) {
        const p = JSON.parse(raw);
        sessionStorage.removeItem("pending_diagnose_photo");
        setPhotoPreview(p.data);
        // Convert data URL → File so run() can upload it
        fetch(p.data).then(r => r.blob()).then(blob => {
          setPhoto(new File([blob], p.name || "photo.jpg", { type: p.type || "image/jpeg" }));
        });
      }
    } catch { /* ignore */ }
  }, []);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Only image files"); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Photo too large (max ${MAX_MB} MB)`); return; }
    setPhoto(f);
    const reader = new FileReader();
    reader.onload = e => setPhotoPreview(e.target.result);
    reader.readAsDataURL(f);
  };
  const clearPhoto = () => { setPhoto(null); setPhotoPreview(null); if (fileRef.current) fileRef.current.value = ""; };

  const run = async () => {
    if (form.description.trim().length < 6) {
      toast.error("Add a short symptom description first"); return;
    }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("description", form.description.trim());
      if (form.address.trim()) fd.append("address", form.address.trim());
      if (form.job_id) fd.append("job_id", form.job_id);
      if (photo) fd.append("photo", photo);
      const r = await api.post("/plumbing/ai-leak-diagnose", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data.saved_to_job) {
        const j = jobs.find(x => x.id === r.data.saved_to_job);
        toast.success(`Saved to job: ${j?.title || "job"}`);
        // Refresh jobs so has_diagnose flag updates
        try { const jr = await api.get("/plumbing/plumbing-jobs"); setJobs(jr.data.jobs || []); }
        catch { /* ignore */ }
      } else {
        toast.success(`Verdict — ${r.data.confidence.toUpperCase()} confidence`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Diagnose failed"));
    } finally { setLoading(false); }
  };

  const clear = () => {
    setForm({ description: "", address: "", job_id: "" });
    setResult(null);
    setActionOpen(null);
    setAttachJobId("");
    clearPhoto();
  };

  // Extract just the JSON fields the backend needs (drops saved_to_job which is
  // set by the initial diagnose call and shouldn't be re-sent).
  const verdictPayload = () => {
    if (!result) return null;
    return {
      likely_cause: result.likely_cause,
      confidence: result.confidence,
      next_steps: result.next_steps || [],
      parts_list: result.parts_list || [],
      est_cents: result.est_cents || 0,
      safety_flag: result.safety_flag || null,
      warranty_note: result.warranty_note || null,
      used_photo: !!result.used_photo,
    };
  };

  const attachToJob = async () => {
    if (!attachJobId) { toast.error("Pick a job first"); return; }
    setSaving(true);
    try {
      const r = await api.post("/plumbing/attach-diagnose", {
        job_id: attachJobId, verdict: verdictPayload(),
      });
      toast.success(`Attached to ${r.data.title || "job"}`);
      nav(`/admin/jobs?open=${r.data.job_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Attach failed"));
    } finally { setSaving(false); }
  };

  const createJobFromVerdict = async () => {
    if (!newJob.property_address.trim()) { toast.error("Address is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/plumbing/create-job-from-diagnose", {
        property_address: newJob.property_address.trim(),
        unit: newJob.unit.trim(),
        title: result.likely_cause.slice(0, 200),
        customer_name: newJob.customer_name.trim(),
        customer_phone: newJob.customer_phone.trim(),
        verdict: verdictPayload(),
      });
      toast.success("Job created");
      nav(`/admin/jobs?open=${r.data.job_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Create failed"));
    } finally { setSaving(false); }
  };

  // When a verdict lands, prefill the new-job address with whatever the tech
  // typed on the form (so the CTA feels one-tap).
  useEffect(() => {
    if (result && !result.saved_to_job) {
      setNewJob(nj => ({ ...nj, property_address: form.address || nj.property_address }));
    }
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  const conf = result ? (CONFIDENCE[result.confidence] || CONFIDENCE.medium) : null;

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="plumbing-diagnose-page">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-blue-600/15 text-blue-600 flex items-center justify-center">
            <Drop weight="fill" size={22}/>
          </div>
          <div>
            <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight" data-testid="plumbing-diagnose-heading">
              AI Leak Diagnose
            </h1>
            <div className="text-xs sm:text-sm text-slate-500 font-medium">
              Snap a photo, type what you're seeing → Claude Haiku returns a verdict + parts + estimate.
            </div>
          </div>
        </div>

        {/* Input card */}
        <div className="bg-white border-2 border-slate-200 rounded-xl p-4 space-y-3" data-testid="plumbing-diagnose-form">
          {/* Photo dropzone */}
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Photo (optional — Claude Vision)</div>
            {photoPreview ? (
              <div className="relative border-2 border-blue-500 rounded-lg overflow-hidden" data-testid="photo-preview">
                <img src={photoPreview} alt="preview" className="w-full max-h-64 object-contain bg-slate-50"/>
                <button type="button" onClick={clearPhoto}
                  data-testid="photo-clear"
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 hover:bg-white text-slate-800 inline-flex items-center justify-center shadow">
                  <X size={14}/>
                </button>
                <div className="absolute bottom-2 left-2 text-[10px] font-black uppercase tracking-widest bg-blue-600 text-white px-2 py-1 rounded inline-flex items-center gap-1">
                  <ImageIcon weight="fill" size={11}/> {photo?.name || "photo"}
                </div>
              </div>
            ) : (
              <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => {
                  e.preventDefault(); setDragging(false);
                  const f = e.dataTransfer?.files?.[0];
                  if (f) handleFile(f);
                }}
                data-testid="photo-dropzone"
                className={`rounded-lg border-2 border-dashed p-6 text-center transition ${
                  dragging ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50"}`}>
                <UploadSimple size={26} className="mx-auto text-slate-500 mb-1.5"/>
                <div className="text-sm font-black text-slate-700">Drop a photo, or pick one below</div>
                <div className="text-[11px] text-slate-500 mt-0.5">JPG · PNG · HEIC — max {MAX_MB} MB</div>
                <div className="mt-3 flex gap-2 justify-center flex-wrap">
                  <button type="button" onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
                    data-testid="photo-take-btn"
                    className="h-9 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                    Take photo
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                    data-testid="photo-pick-btn"
                    className="h-9 px-3 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-900 text-slate-800 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                    Choose from library
                  </button>
                </div>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*"
              className="hidden" data-testid="photo-file-input"
              onChange={e => handleFile(e.target.files?.[0])}/>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment"
              className="hidden" data-testid="photo-camera-input"
              onChange={e => handleFile(e.target.files?.[0])}/>
          </div>

          <Field label="Symptom description*">
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Kitchen ½-turn shut-off dripping onto cabinet floor. Corrosion on inlet nut."
              rows={3} data-testid="plumbing-diagnose-desc"
              className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg font-medium text-sm resize-y"/>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Address (optional)">
              <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                placeholder="1200 Sample St, Philadelphia PA"
                data-testid="plumbing-diagnose-address"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
            </Field>
            <Field label="Attach to a job (optional)">
              <select value={form.job_id} onChange={e => setForm({ ...form, job_id: e.target.value })}
                data-testid="plumbing-diagnose-job"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
                <option value="">— No job (one-off)</option>
                {jobs.map(j => (
                  <option key={j.id} value={j.id}>
                    {j.has_diagnose ? "✓ " : ""}{j.title} — {j.address}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={run} disabled={loading}
              data-testid="plumbing-diagnose-run"
              className="flex-1 h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <Sparkle weight="fill" size={14}/> {loading ? "Diagnosing…" : "Run AI Diagnose"}
            </button>
            {(result || photo) && (
              <button type="button" onClick={clear}
                data-testid="plumbing-diagnose-clear"
                className="h-11 px-3 rounded-lg border-2 border-slate-200 hover:border-slate-400 text-slate-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                <X size={12}/> Clear
              </button>
            )}
          </div>
        </div>

        {/* Verdict card */}
        {result && (
          <div className="mt-4 bg-white border-2 border-blue-200 rounded-xl overflow-hidden" data-testid="plumbing-diagnose-result">
            <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white p-4">
              <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Sparkle weight="fill" size={16}/>
                  <div className="text-[10px] font-black uppercase tracking-widest">Claude Haiku · Verdict</div>
                  {result.used_photo && (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-white/10 text-blue-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                      <Camera weight="fill" size={9}/> Photo used
                    </span>
                  )}
                </div>
                <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${conf.cls}`}
                        data-testid="plumbing-diagnose-confidence">
                  {conf.label}
                </span>
              </div>
              <div className="font-display font-black text-lg leading-snug" data-testid="plumbing-diagnose-cause">
                {result.likely_cause}
              </div>
              <div className="mt-3 text-[11px] font-black uppercase tracking-widest text-blue-200">
                Customer estimate
              </div>
              <div className="font-display font-black text-3xl" data-testid="plumbing-diagnose-est">
                {dollars(result.est_cents)}
              </div>
              {result.saved_to_job && (
                <div className="mt-3 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 text-[11px] font-black uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1" data-testid="plumbing-diagnose-saved">
                  <CheckCircle weight="fill" size={11}/> Saved to job — description + estimate updated
                </div>
              )}
            </div>

            {/* ── ACTION ROW ──────────────────────────────────────── */}
            {result.saved_to_job ? (
              <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200 flex flex-wrap gap-2" data-testid="plumbing-diagnose-actions-saved">
                <button type="button" onClick={() => nav(`/admin/jobs?open=${result.saved_to_job}`)}
                  data-testid="plumbing-diagnose-open-job"
                  className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  <ArrowRight weight="bold" size={13}/> Open job
                </button>
              </div>
            ) : (
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200" data-testid="plumbing-diagnose-actions">
                {actionOpen === null && (
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setActionOpen("attach")}
                      data-testid="plumbing-diagnose-attach-btn"
                      className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                      <LinkIcon weight="bold" size={13}/> Attach to existing job
                    </button>
                    <button type="button" onClick={() => setActionOpen("create")}
                      data-testid="plumbing-diagnose-create-btn"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                      <Plus weight="bold" size={13}/> Create new job
                    </button>
                  </div>
                )}

                {actionOpen === "attach" && (
                  <div className="space-y-2" data-testid="plumbing-diagnose-attach-form">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">Pick a job to attach to</div>
                    <select value={attachJobId} onChange={e => setAttachJobId(e.target.value)}
                      data-testid="plumbing-diagnose-attach-select"
                      className="w-full h-10 px-3 border-2 border-slate-200 rounded-lg font-medium text-sm">
                      <option value="">— Select job —</option>
                      {jobs.map(j => (
                        <option key={j.id} value={j.id}>
                          {j.title} · {j.address}{j.has_diagnose ? " (has diagnose)" : ""}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <button type="button" onClick={attachToJob} disabled={saving || !attachJobId}
                        data-testid="plumbing-diagnose-attach-submit"
                        className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                        {saving ? "Attaching…" : (<><CheckCircle weight="bold" size={13}/> Attach</>)}
                      </button>
                      <button type="button" onClick={() => { setActionOpen(null); setAttachJobId(""); }}
                        className="h-10 px-3 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-900 text-slate-800 text-[11px] font-black uppercase tracking-widest">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {actionOpen === "create" && (
                  <div className="space-y-2" data-testid="plumbing-diagnose-create-form">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">Create a new job</div>
                    <input list="known-addresses" autoComplete="off" type="text" value={newJob.property_address}
                      onChange={e => setNewJob({ ...newJob, property_address: e.target.value })}
                      placeholder="Property address*"
                      data-testid="plumbing-diagnose-newjob-address"
                      className="w-full h-10 px-3 border-2 border-slate-200 rounded-lg font-medium text-sm"/>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" value={newJob.unit}
                        onChange={e => setNewJob({ ...newJob, unit: e.target.value })}
                        placeholder="Unit (optional)"
                        data-testid="plumbing-diagnose-newjob-unit"
                        className="h-10 px-3 border-2 border-slate-200 rounded-lg font-medium text-sm"/>
                      <input type="text" value={newJob.customer_name}
                        onChange={e => setNewJob({ ...newJob, customer_name: e.target.value })}
                        placeholder="Customer name (optional)"
                        data-testid="plumbing-diagnose-newjob-name"
                        className="h-10 px-3 border-2 border-slate-200 rounded-lg font-medium text-sm"/>
                    </div>
                    <input type="tel" value={newJob.customer_phone}
                      onChange={e => setNewJob({ ...newJob, customer_phone: e.target.value })}
                      placeholder="Customer phone (optional)"
                      data-testid="plumbing-diagnose-newjob-phone"
                      className="w-full h-10 px-3 border-2 border-slate-200 rounded-lg font-medium text-sm"/>
                    <div className="text-[10px] text-slate-500">
                      Title & description will be auto-filled from the AI verdict. Estimate: <span className="font-black text-slate-900">{dollars(result.est_cents)}</span>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={createJobFromVerdict} disabled={saving || !newJob.property_address.trim()}
                        data-testid="plumbing-diagnose-create-submit"
                        className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                        {saving ? "Creating…" : (<><Plus weight="bold" size={13}/> Create job</>)}
                      </button>
                      <button type="button" onClick={() => setActionOpen(null)}
                        className="h-10 px-3 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-900 text-slate-800 text-[11px] font-black uppercase tracking-widest">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="p-4 space-y-3">
              {result.safety_flag && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2" data-testid="plumbing-diagnose-safety">
                  <ShieldWarning weight="fill" size={16} className="text-amber-600 shrink-0 mt-0.5"/>
                  <div className="text-xs text-amber-900 leading-relaxed">
                    <span className="font-black uppercase text-[10px] tracking-widest mr-1">Safety</span>
                    {result.safety_flag}
                  </div>
                </div>
              )}

              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1">
                  <ArrowRight size={12}/> Next steps
                </div>
                <ol className="space-y-1.5" data-testid="plumbing-diagnose-steps">
                  {result.next_steps.map((s, i) => (
                    <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
                      <span className="h-5 w-5 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center font-black text-[10px] shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <span className="leading-snug">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1">
                  <Package size={12}/> Parts list
                </div>
                <div className="space-y-1" data-testid="plumbing-diagnose-parts">
                  {result.parts_list.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-800">
                      <span className="h-5 w-8 rounded bg-slate-900 text-white text-[10px] font-mono flex items-center justify-center shrink-0">
                        ×{p.qty}
                      </span>
                      <span className="leading-snug">{p.item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {result.warranty_note && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2" data-testid="plumbing-diagnose-warranty">
                  <Trophy weight="fill" size={14} className="text-emerald-600 shrink-0 mt-0.5"/>
                  <div className="text-[11px] text-emerald-900 leading-relaxed">
                    <span className="font-black uppercase text-[10px] tracking-widest mr-1">Warranty</span>
                    {result.warranty_note}
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-slate-100 text-[10px] text-slate-400 leading-snug">
                AI-generated verdict — always verify parts sizing on-site before quoting. Field Radar is a diagnostic
                assist tool, not a substitute for a licensed plumber's judgment.
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
